import { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "./supabase";

const AuthContext = createContext({});

// Every navigable tab + top-module the CRM has. Used by the Users & Roles UI
// so admins can grant/revoke each one. Keep in sync with NAV in elyon-core.jsx.
const ALL_MODULES = [
  // Top-level modules (the chips in the dark topbar)
  'pay', 'marketing', 'aiagent',
  // Operations
  'dashboard', 'frontdesk', 'appointments', 'checkout', 'takepay', 'messages',
  // Clinical
  'clients', 'gfe', 'providers', 'consents', 'patientpaperwork',
  // Business
  'memberships', 'services', 'packages', 'inventory', 'payroll',
  // Growth
  'bookinglink', 'sales', 'analytics', 'clientinsights', 'winback',
  'mission', 'leadengine', 'pipeline', 'botoxrecall', 'treatments',
  // Platform / Settings
  'users', 'ai', 'bottraining', 'automations',
];

// ★ Clinical roles that ALWAYS see patient paperwork (consents, intake forms,
//   pre/post care, clinical note templates). Owner directive 2026-06-10: NPs
//   and nurses need this to do their job — they shouldn't have to wait for an
//   admin to flip a permission. Match is case-insensitive.
const CLINICAL_ROLES = new Set([
  "np", "nurse", "rn", "provider", "esthetician", "clinical", "medical",
  "nurse-practitioner", "nurse practitioner", "physician", "doctor", "pa",
]);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const applySession = (session) => {
      if (cancelled) return;
      if (session?.user) {
        setUser(session.user);
        // ★ Deadlock fix: NEVER run Supabase queries synchronously inside the
        //   onAuthStateChange callback. supabase-js holds an internal auth lock
        //   while callbacks run; a query that needs a token refresh waits on
        //   that same lock → the query never resolves → the app hangs on
        //   "Loading..." forever (typically first open after the JWT expired).
        //   setTimeout(0) defers the queries until after the lock is released.
        setTimeout(() => { if (!cancelled) loadProfileAndPermissions(session.user.id); }, 0);
      } else {
        setUser(null);
        setProfile(null);
        setPermissions({});
        setLoading(false);
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => applySession(session));

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => applySession(session));

    // Failsafe: if auth/profile loading still hangs (dead network, corrupt
    // token), drop the spinner after 10s instead of blocking the CRM forever.
    const failsafe = setTimeout(() => { if (!cancelled) setLoading(false); }, 10000);

    return () => { cancelled = true; clearTimeout(failsafe); subscription.unsubscribe(); };
  }, []);

  const loadProfileAndPermissions = async (userId) => {
    try {
      const { data: prof } = await supabase.from("profiles").select("*").eq("id", userId).single();
      setProfile(prof);

      const { data: perms } = await supabase.from("role_permissions").select("*");
      if (perms) {
        const permMap = {};
        perms.forEach(p => { permMap[p.role] = p.modules || []; });
        setPermissions(permMap);
      }
    } finally {
      // Guaranteed even if a query throws — the app must never stay stuck
      // on the loading screen because of a failed profile fetch.
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setPermissions({});
  };

  const hasAccess = (module) => {
    if (!profile) return false;
    if (profile.role === "admin") return true;
    // ★ Clinical roles always get patient paperwork — they can't sign client
    //   consents or pull pre/post-care without it, and waiting on an admin to
    //   flip a permission would block daily ops.
    const role = (profile.role || "").toLowerCase().trim();
    if (CLINICAL_ROLES.has(role) && module === "patientpaperwork") return true;
    return permissions[profile.role]?.includes(module) || false;
  };

  // ★ Sales / revenue visibility (owner directive 2026-06-16): only the owner
  //   (admin), the director, and NP Sarah may see sales/revenue figures in the
  //   Dashboard and POS. Everyone else (front desk, other nurses, etc.) is
  //   blocked — even if they otherwise have the 'sales' module. Individuals
  //   beyond admin/director are granted via profiles.can_view_sales = true.
  const canViewSales = !!profile && (
    ["admin", "owner", "director"].includes((profile.role || "").toLowerCase().trim())
    || profile.can_view_sales === true
  );

  const canAccessTopModule = (mod) => {
    if (!profile) return false;
    if (profile.role === "admin") return true;
    const topModuleMap = { core: 'dashboard', pay: 'pay', marketing: 'marketing', shop: 'marketing', ai: 'aiagent' };
    return hasAccess(topModuleMap[mod]);
  };

  const refreshPermissions = async () => {
    const { data: perms } = await supabase.from("role_permissions").select("*");
    if (perms) {
      const permMap = {};
      perms.forEach(p => { permMap[p.role] = p.modules || []; });
      setPermissions(permMap);
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, permissions, loading, signIn, signOut, hasAccess, canAccessTopModule, canViewSales, refreshPermissions, ALL_MODULES }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
